import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { action, computed } from "@ember/object";
import { service } from "@ember/service";
import DButton from "discourse/components/d-button";
import concatClass from "discourse/helpers/concat-class";
import { popupAjaxError } from "discourse/lib/ajax-error";
import cookie from "discourse/lib/cookie";
import { i18n } from "discourse-i18n";
import CirclesMembershipReasonModal from "../modal/membership-reason";

export default class CirclesMembershipButton extends Component {
  @service appEvents;
  @service currentUser;
  @service dialog;
  @service router;
  @service siteSettings;
  @service modal;

  @tracked updatingMembership = false;

  @computed(
    "canJoinCircle",
    "canManageCircle",
    "canLeaveCircle",
    "canCancelMembershipRequest",
    "canRequestMembership"
  )
  get data() {
    if (this.canJoinCircle) {
      return {
        className: "circles-join-button btn-primary",
        icon: "user-plus",
        label: "discourse_circles.circle.actions.join",
        onClick: this.joinCircle,
      };
    } else if (this.canManageCircle) {
      return {
        className: "circles-manage-button btn-warning",
        icon: "wrench",
        label: "discourse_circles.circle.actions.manage",
        onClick: this.manageCircle,
      };
    } else if (this.canLeaveCircle) {
      return {
        className: "circles-leave-button btn-danger",
        icon: "wrench",
        label: "discourse_circles.circle.actions.leave",
        onClick: this.leaveCircle,
      };
    } else if (this.canCancelMembershipRequest) {
      return {
        className: "circles-cancel-request-button btn-default",
        icon: "user-slash",
        label: "discourse_circles.circle.actions.cancel_request",
        onClick: this.cancelMembershipRequest,
      };
    } else if (this.canRequestMembership) {
      return {
        className: "circles-request-button btn-primary",
        icon: "user-plus",
        label: "discourse_circles.circle.actions.request",
        onClick: this.showRequestMembershipForm,
      };
    }
  }

  @computed("data.className")
  get className() {
    return this.data?.className;
  }

  @computed("updatingMembership", "data.icon")
  get icon() {
    return this.data?.icon;
  }

  @computed("updatingMembership", "data.label")
  get label() {
    const translatedLabel = i18n(this.data?.label);

    return this.updatingMembership
      ? i18n(this.data?.label + "_loading", {
          defaultValue: translatedLabel,
        })
      : translatedLabel;
  }

  @computed("data.onClick")
  get onClick() {
    return this.data?.onClick;
  }

  @computed("args.model.member_group.public_admission", "userIsCircleUser")
  get canJoinCircle() {
    return (
      this.args.model.member_group.public_admission && !this.userIsCircleUser
    );
  }

  @computed("args.model.member_group.public_exit", "userIsCircleUser")
  get canLeaveCircle() {
    return this.args.model.member_group.public_exit && this.userIsCircleUser;
  }

  @computed(
    "args.model.member_group.allow_membership_requests",
    "userIsGroupUser"
  )
  get canRequestMembership() {
    return (
      this.args.model.member_group.allow_membership_requests &&
      !this.userIsCircleUser
    );
  }

  @computed("args.model.is_membership_request_pending")
  get canCancelMembershipRequest() {
    return this.args.model.is_membership_request_pending;
  }

  @computed("args.model.is_circle_owner")
  get canManageCircle() {
    return !!this.args.model.is_circle_owner;
  }

  @computed("args.model.is_circle_member")
  get userIsCircleUser() {
    return !!this.args.model.is_circle_member;
  }

  async #join() {
    this.updatingMembership = true;
    try {
      const circle = this.args.model;
      const group = circle.member_group;

      await group.join();

      circle.set("is_circle_member", true);
      group.set("is_group_user", true);

      this.appEvents.trigger("circle:join", circle);
      this.appEvents.trigger("group:join", group);
    } catch (e) {
      popupAjaxError(e);
    } finally {
      this.updatingMembership = false;
    }
  }

  async #leave() {
    this.updatingMembership = true;
    try {
      const circle = this.args.model;

      await circle.leave();

      circle.set("is_circle_member", false);
      this.appEvents.trigger("circle:leave", circle);

      circle.groups.forEach((group) => {
        group.set("is_group_user", false);
        this.appEvents.trigger("group:leave", group);
      });
    } catch (e) {
      popupAjaxError(e);
    } finally {
      this.updatingMembership = false;
    }
  }

  async #cancelRequest() {
    this.updatingMembership = true;
    try {
      const circle = this.args.model;

      await circle.cancelMembershipRequest();

      circle.set("is_membership_request_pending", false);
      this.appEvents.trigger("circle:membership_request_cancelled", circle);
    } catch (e) {
      popupAjaxError(e);
    } finally {
      this.updatingMembership = false;
    }
  }

  #showLoginModal(evt) {
    evt?.preventDefault();
    evt?.stopPropagation();

    cookie("destination_url", window.location.href);
    this.args.showLogin();
  }

  @action
  joinCircle(evt) {
    if (this.currentUser) {
      if (
        this.siteSettings
          .discourse_circles_ask_confirmation_before_user_joins_circle
      ) {
        return this.dialog.confirm({
          title: i18n("discourse_circles.circle.confirm_join.title", {
            name: this.args.model.name,
          }),
          message: i18n("discourse_circles.circle.confirm_join.question", {
            name: this.args.model.name,
          }),
          confirmButtonLabel: "discourse_circles.circle.confirm_join.confirm",
          cancelButtonLabel: "cancel",
          class: "circles-confirm-join-dialog",
          didConfirm: () => this.#join(),
        });
      } else {
        this.#join();
      }
    } else {
      this.#showLoginModal(evt);
    }
  }

  @action
  manageCircle() {
    this.router.transitionTo(
      "circle.manage",
      this.args.model.slug_path_with_id
    );
  }

  @action
  leaveCircle() {
    if (
      this.args.model.member_group.public_admission &&
      !this.siteSettings
        .discourse_circles_always_ask_confirmation_before_user_leaves_circle
    ) {
      this.#leave();
    } else {
      return this.dialog.confirm({
        title: i18n("discourse_circles.circle.confirm_leave.title", {
          name: this.args.model.name,
        }),
        message: i18n("discourse_circles.circle.confirm_leave.question", {
          name: this.args.model.name,
        }),
        confirmButtonLabel: "discourse_circles.circle.confirm_leave.confirm",
        cancelButtonLabel: "cancel",
        class: "circles-confirm-leave-dialog",
        didConfirm: () => this.#leave(),
      });
    }
  }

  @action
  showRequestMembershipForm(evt) {
    const circle = this.args.model;

    if (this.currentUser) {
      this.modal.show(CirclesMembershipReasonModal, {
        model: {
          circle,
          // because controllers are singletons. ensures that the reason will
          // be empty
          reason: "",
          mode: "request",
          onSubmit: async (reason) => {
            this.updatingMembership = true;

            try {
              await circle.requestMembership(reason);

              circle.set("is_membership_request_pending", true);
              this.appEvents.trigger("circle:request_membership", circle);

              this.dialog.alert({
                message: i18n(
                  "discourse_circles.circle.membership_request.success",
                  {
                    circle_name: circle.name,
                  }
                ),
                class: "circles-alert-membership-requested-dialog",
              });
            } catch (e) {
              popupAjaxError(e);
            } finally {
              this.updatingMembership = false;
            }
          },
        },
      });
    } else {
      this.#showLoginModal(evt);
    }
  }

  @action
  cancelMembershipRequest() {
    if (
      !this.siteSettings
        .discourse_circles_always_ask_confirmation_before_user_leaves_circle
    ) {
      this.#cancelRequest();
    } else {
      return this.dialog.confirm({
        title: i18n("discourse_circles.circle.confirm_cancel_request.title", {
          name: this.args.model.name,
        }),
        message: i18n(
          "discourse_circles.circle.confirm_cancel_request.question",
          {
            name: this.args.model.name,
          }
        ),
        confirmButtonLabel:
          "discourse_circles.circle.confirm_cancel_request.confirm",
        cancelButtonLabel:
          "discourse_circles.circle.confirm_cancel_request.abort",
        class: "circles-confirm-cancel-request-dialog",
        didConfirm: () => this.#cancelRequest(),
      });
    }
  }

  <template>
    {{#if this.data}}
      <DButton
        class={{concatClass "circles-membership-button" this.className}}
        ...attributes
        @action={{this.onClick}}
        @disabled={{this.updatingMembership}}
        @icon={{this.icon}}
        @isLoading={{this.updatingMembership}}
        @translatedLabel={{this.label}}
      />
    {{else}}
      {{yield}}
    {{/if}}
  </template>
}
